import {
  IAnswer,
  IQuestion,
  IQuestionnaireDetails,
  IQuestionnaireSubmission,
  IQuestionOption,
} from '../../eventInterface';

import * as Yup from 'yup';
import i18n from '../../../../i18n';

const getRequiredFieldByDetailsMap = (t: any): Map<string, any> => {
  return new Map([
    ['firstName', { title: t('event.form.firstNameLabel'), controlType: 'Input', required: true }],
    ['lastName', { title: t('event.form.lastNameLabel'), controlType: 'Input', required: true }],
    ['email', { title: t('event.form.emailLabel'), controlType: 'Input', required: true }],
    [
      'humanRegistrantToken',
      { title: t('event.form.humanRegistrantTokenLabel'), controlType: null, required: false },
    ],
  ]);
};

const getQuestionNameByQuestionDetailsMap = (
  questions: IQuestion[],
  filterFunction: (question: IQuestion) => boolean,
  questionName?: string,
): Map<string, any> => {
  const questionNameByDetailsMap = new Map();

  questions.filter(filterFunction).map((question, index) => {
    let value = {
      alias: question.alias,
      title: question.title,
      questionKey: question.questionKey,
      controlType: getControlType(question),
      required: question.required,
      sortOrder: question.sortOrder,
      questionType: question.questionType,
      isTemplateQuestion: question.readOnly,
      options: getOptions(question),
    };

    if (question.alias == 'country') {
      /* Todo: Handling the countries use-case at UI. But list of countries should be provided from backend api.*/
      value = {
        ...value,
        controlType: 'Dropdown',
        options: getOptionsFromArr(i18n.t('countries', { returnObjects: true })),
      };
    }

    if (questionName) {
      questionNameByDetailsMap.set(questionName + index, value);
    } else {
      questionNameByDetailsMap.set(sanitizeString(question.alias || question.title), value);
    }
  });

  return questionNameByDetailsMap;
};

const getControlType = (question: IQuestion): string => {
  let controlType: string;

  switch (question.questionType) {
    case 'MULTIPLE_CHOICE':
      controlType = 'Dropdown';
      break;
    case 'SHORT_ANSWER':
      controlType = question.readOnly ? 'Input' : 'TextArea';
      break;
    case 'ESSAY':
      controlType = 'TextArea';
      break;
    default:
      controlType = 'Input';
  }

  return controlType;
};

const getOptions = (question: IQuestion): IQuestionOption[] => {
  const options: IQuestionOption[] = [];

  question.options
    .map((option) => {
      return {
        optionKey: option.optionKey,
        title: option.title,
        sortOrder: option.sortOrder,
      };
    })
    .sort((option1, option2) => (option1.sortOrder > option2.sortOrder ? 1 : -1))
    .forEach((option) => options.push(option));
  return options;
};

const getOptionsFromArr = (metadata: string[]): any => {
  const options: any[] = [];

  metadata.map((item, index) => {
    const option = {
      optionKey: item,
      title: item,
      sortOrder: index,
    };
    options.push(option);
  });
  return options;
};

const initializeFormObjForQuestionnaire = (
  initialValuesObj: any,
  questions: IQuestion[],
  filterFunction: (question: IQuestion) => boolean,
  questionName?: string,
): any => {
  questions.filter(filterFunction).map((question, index) => {
    if (questionName) {
      initialValuesObj[questionName + index] = '';
    } else {
      initialValuesObj[sanitizeString(question.alias || question.title)] = '';
    }
  });

  return initialValuesObj;
};

const getFormInitialValuesWithQuestionnaireInitialValues = (
  mergeObj: any,
  questions: IQuestion[],
): any => {
  const createInitialValueObjForTemplateQuestions = initializeFormObjForQuestionnaire(
    {},
    questions,
    (question) => question.readOnly,
  );

  const createInitialValueObjForCustomQuestions = initializeFormObjForQuestionnaire(
    {},
    questions,
    (question) => !question.readOnly,
    'customQuestion',
  );

  return {
    ...mergeObj,
    ...createInitialValueObjForTemplateQuestions,
    ...createInitialValueObjForCustomQuestions,
  };
};

const createQuestionnaireSubmissionObj = (
  questionnaireDetails: IQuestionnaireDetails | undefined,
  answers: IAnswer[],
): IQuestionnaireSubmission | null => {
  let questionnaireSubmission: IQuestionnaireSubmission | null = null;
  let questionnaireKey: string;
  let questionnaireVersion: string;

  if (questionnaireDetails) {
    questionnaireKey = questionnaireDetails.questionnaireKey;
    questionnaireVersion = String(questionnaireDetails.questionnaireVersion);

    questionnaireSubmission = {
      questionnaireKey: questionnaireKey,
      questionnaireVersion: questionnaireVersion,
      answers: answers,
    };
  }
  return questionnaireSubmission;
};

const createRegistrationSubmissionObj = (
  fieldNameByDetailsMap: Map<string, any> | null,
  questionnaireDetails: IQuestionnaireDetails | undefined,
  submittedFormDetails: any,
): any => {
  let registrationSubmissionRequest: any = null;

  if (fieldNameByDetailsMap && submittedFormDetails) {
    const { firstName, lastName, email, humanRegistrantToken } = submittedFormDetails;
    let answers: (null | IAnswer)[] = [];

    answers = Object.entries(submittedFormDetails)
      .filter(([fieldName, fieldValue]) => fieldValue)
      .map(([fieldName, fieldValue]) => {
        let answer: IAnswer | null = null;
        const fieldDetails = fieldNameByDetailsMap.get(fieldName);

        /* firstName, lastName and email will not have questionKey and options and should not be added as question/answer in questionnaire submission request
         * Note : isTermsConditionChecked will have null fieldDetails since it is not added in fieldNameByDetailsMap since it is not similar to other fields in the form */
        if (
          fieldDetails?.alias != 'country' &&
          fieldDetails?.questionKey &&
          fieldDetails.options?.length &&
          String(fieldValue) &&
          String(fieldValue).trim()
        ) {
          answer = {
            questionKey: fieldDetails.questionKey,
            selectedOptions: [{ optionKey: String(fieldValue) }],
          };
        } else if (fieldDetails?.questionKey && String(fieldValue) && String(fieldValue).trim()) {
          answer = {
            questionKey: fieldDetails.questionKey,
            answerText: String(fieldValue),
          };
        }
        return answer;
      });

    registrationSubmissionRequest = {
      firstName,
      lastName,
      email,
      humanRegistrantToken,
    };

    /* answers array can have null values i.e in case of firstName, lastName and email */
    const filteredAnswers = answers.filter((value) => value) as IAnswer[];

    const questionnaireSubmission = createQuestionnaireSubmissionObj(
      questionnaireDetails,
      filteredAnswers,
    );

    if (questionnaireSubmission) {
      registrationSubmissionRequest['questionnaireSubmission'] = questionnaireSubmission;
    }
  }

  return registrationSubmissionRequest;
};

const getValidationSchemaForRegForm = (map: Map<string, any>, questionnaireDetails: any): any => {
  const schemaObj: any = {};

  for (const [name, details] of map) {
    const requiredMsg = `${details?.title} ${i18n.t('event.form.isRequired')}`;
    if (name === 'firstName') {
      schemaObj[name] = Yup.string()
        .trim()
        .max(15, i18n.t('event.form.firstNameError'))
        .required(requiredMsg);
    } else if (name === 'lastName') {
      schemaObj[name] = Yup.string()
        .trim()
        .max(20, i18n.t('event.form.lastNameError'))
        .required(requiredMsg);
    } else if (name === 'email') {
      schemaObj[name] = Yup.string()
        .trim()
        .email(i18n.t('event.form.emailError'))
        .required(requiredMsg);
    } else if (details.alias === 'phone') {
      const phoneRegExp = /^[-+ ()0-9]{6,20}$/;
      schemaObj[name] = Yup.string().trim().matches(phoneRegExp, i18n.t('event.form.phoneError'));
      if (details.required) {
        schemaObj[name] = schemaObj[name].required(requiredMsg);
      }
    } else if (
      details.required &&
      !details.isTemplateQuestion &&
      details.questionType === 'MULTIPLE_CHOICE'
    ) {
      schemaObj[name] = Yup.string()
        .trim()
        .required(i18n.t('event.form.customMultipleChoiceQuestionError'));
    } else if (details.required) {
      if (details.controlType === 'Dropdown') {
        schemaObj[name] = Yup.string().trim().required(requiredMsg).min(2, requiredMsg);
      } else {
        schemaObj[name] = Yup.string().trim().required(requiredMsg);
      }
    }
  }

  if (questionnaireDetails?.customDisclaimerEnabled) {
    schemaObj['isDisclaimerChecked'] = Yup.boolean().oneOf(
      [true],
      i18n.t('event.form.disclaimerError'),
    );
  }

  return Yup.object().shape(schemaObj);
};

const sortedEntries = (map: Map<string, any>): Array<[string, any]> => {
  const entries = Array.from(map);
  return entries.sort((firstEntry, secondEntry) => {
    const [, firstEntryValue] = firstEntry;
    const [, secondEntryValue] = secondEntry;

    return firstEntryValue?.sortOrder > secondEntryValue?.sortOrder ? 1 : -1;
  });
};

const sanitizeString = (str: string): string => {
  return str.replace(/[.\s]+/g, '');
};

const createMixPanelData = (
  questionnaireDetails: any,
  formValues: any,
  fieldNameByDetailsMap: any,
) => {
  const obj = {} as { [key: string]: any };
  const questions = questionnaireDetails?.questions;
  for (const field in formValues) {
    const isQuestion =
      questions && questions.find((question: any) => sanitizeString(question.title) === field);
    if (isQuestion) {
      if (isQuestion.options?.length) {
        isQuestion['answer'] = isQuestion.options.find(
          (option: any) => option.optionKey === formValues[field],
        )?.title;
      } else {
        isQuestion['answer'] = formValues[field];
      }
      obj[field] = isQuestion;
    } else {
      const fieldDetails = fieldNameByDetailsMap.get(field);
      if (fieldDetails?.questionKey) {
        fieldDetails['answer'] = formValues[field];
        obj[fieldDetails.title] = fieldDetails;
      } else {
        obj[field] = formValues[field];
      }
    }
  }
  return obj;
};
export {
  getRequiredFieldByDetailsMap,
  getControlType,
  getOptions,
  getQuestionNameByQuestionDetailsMap,
  initializeFormObjForQuestionnaire,
  getFormInitialValuesWithQuestionnaireInitialValues,
  createQuestionnaireSubmissionObj,
  createRegistrationSubmissionObj,
  getValidationSchemaForRegForm,
  sortedEntries,
  sanitizeString,
  createMixPanelData,
};
